import React from 'react';
import { CheckCircle2, Briefcase, Landmark } from 'lucide-react';
import { ROLES } from '../../data/framework';
import { questionnaireSize } from '../../utils/framework';
import type { RoleGroup } from '../../types/framework';
type RoleStepProps = {
  type: string;
  roleId: string | null;
  onTypeChange: (type: string) => void;
  onRoleSelect: (roleId: string) => void;
};
const ASSESSMENT_TYPES = [{
  id: 'self',
  title: 'Self-Assessment',
  desc: 'Individuals evaluate their own performance.'
}, {
  id: '180',
  title: '180° Feedback',
  desc: 'Manager and self-evaluation combined.'
}, {
  id: '360',
  title: '360° Feedback',
  desc: 'Comprehensive feedback from peers, managers, and direct reports.'
}];
const GROUPS: {
  id: RoleGroup;
  icon: typeof Briefcase;
}[] = [{
  id: 'Executive',
  icon: Briefcase
}, {
  id: 'Board',
  icon: Landmark
}];
export function RoleStep({
  type,
  roleId,
  onTypeChange,
  onRoleSelect
}: RoleStepProps) {
  return <div className="space-y-8 animate-in fade-in duration-300" data-id="element-158" data-mp="2k2n04v86esfl">
      <div data-id="element-159" data-mp="2k47r6ava7t8p">
        <h2 className="text-xl font-semibold text-slate-900" data-id="element-160" data-mp="2fbeortnacwzs">
          Assessment Type &amp; Role
        </h2>
        <p className="text-sm text-slate-500 mt-1" data-id="element-161" data-mp="2ac22pb48nty9">
          Choose the feedback structure, then the role being assessed. The
          role determines the competency domains in the questionnaire.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-162" data-mp="3318y418ec3fa">
        {ASSESSMENT_TYPES.map(t => {
        const selected = type === t.id;
        return <button key={t.id} type="button" onClick={() => onTypeChange(t.id)} aria-pressed={selected} className={`p-4 border rounded-xl text-left transition-all ${selected ? 'border-slate-900 bg-slate-50 ring-1 ring-slate-900' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'}`} data-id="element-163" data-mp="2t2t6zy9dcu5s">
              <span className="flex justify-between items-start mb-2" data-id="element-164" data-mp="1do5m468swyn1">
                <span className="font-medium text-slate-900" data-id="element-165" data-mp="1f4ja8npwlllr">{t.title}</span>
                {selected && <CheckCircle2 className="h-5 w-5 text-slate-900" data-id="element-166" data-mp="1k19or6m28952" />}
              </span>
              <span className="block text-sm text-slate-500" data-id="element-167" data-mp="155twn751inbb">{t.desc}</span>
            </button>;
      })}
      </div>

      <div className="border-t border-slate-200 pt-6 space-y-6" data-id="element-168" data-mp="1a2k8tjdq4yrg">
        <div data-id="element-169" data-mp="wcgqv4lvnr7t">
          <h3 className="text-sm font-medium text-slate-900" data-id="element-170" data-mp="wbyn7mwvyapv">
            Role being assessed *
          </h3>
          <p className="text-sm text-slate-500 mt-0.5" data-id="element-171" data-mp="11506e744kwf9">
            Each role has its own set of competency domains.
          </p>
        </div>

        {GROUPS.map(group => {
        const GroupIcon = group.icon;
        return <div key={group.id} data-id="element-172" data-mp="dduq5qu9w24f">
              <div className="flex items-center text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3" data-id="element-173" data-mp="ddmg06pjbawx">
                <GroupIcon className="h-3.5 w-3.5 mr-1.5" data-id="element-174" data-mp="he6bz6ydg8hh" />
                {group.id}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-175" data-mp="3ux3bkdfd7475">
                {ROLES.filter(r => r.group === group.id).map(role => {
              const selected = roleId === role.id;
              return <button key={role.id} type="button" onClick={() => onRoleSelect(role.id)} aria-pressed={selected} className={`p-4 border rounded-lg text-left transition-all ${selected ? 'border-slate-900 bg-slate-50 ring-1 ring-slate-900' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'}`} data-id="element-176" data-mp="2t0sculv64cc">
                      <span className="flex items-start justify-between gap-2" data-id="element-177" data-mp="mi1gwhotwc36">
                        <span className="text-sm font-medium text-slate-900" data-id="element-178" data-mp="10fq3pv2ghtav">
                          {role.name}
                        </span>
                        {selected && <CheckCircle2 className="h-4 w-4 text-slate-900 shrink-0" data-id="element-179" data-mp="1fk2mslgt6xt6" />}
                      </span>
                      <span className="block text-xs text-slate-500 mt-1.5" data-id="element-180" data-mp="1jjl7bg4px6vh">
                        {role.domains.length} domains ·{' '}
                        {questionnaireSize(role)} questions
                      </span>
                    </button>;
            })}
              </div>
            </div>;
      })}
      </div>
    </div>;
}